import { useQueries } from '@tanstack/react-query'
import { CalendarDays, ChevronDown, Redo2, Undo2 } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { changelog as changelogApi } from '@/api/endpoints'
import { EmptyState, Spinner } from '@/components/ui'
import { Changes } from '@/features/changelog/ChangelogPage'
import RevertModal from '@/features/changelog/RevertModal'
import { useBootstrap } from '@/hooks/useBootstrap'
import { unzoom } from '@/lib/uiScale'
import { cn, formatDateTime } from '@/lib/utils'
import type { ChangeEntry, ChangeKind } from '@/types/api'

/** Zmeny z kalendára - klient a jeho rezervácia, blok inštruktora. Profil
 *  inštruktora (`instructors`) sem zámerne nepatrí, tá záložka je v `/changelog`. */
const KINDS: ChangeKind[] = ['client-reservations', 'instructor-reservations', 'clients']

/** Koľko posledných zmien z každej záložky stačí prehľadať - zoznam sa aj tak
 *  orezáva na `LIMIT` najnovších spomedzi všetkých troch. */
/** Štítok druhu záznamu - klient a inštruktor sa v zozname inak nerozlíšia. */
const KIND_STYLE: Record<ChangeKind, string> = {
  'client-reservations': 'bg-sky-100 text-sky-800',
  'instructor-reservations': 'bg-amber-100 text-amber-800',
  clients: 'bg-slate-100 text-slate-700',
  instructors: 'bg-slate-100 text-slate-700',
}

const PER_KIND = 8
const LIMIT = 8

interface Entry {
  kind: ChangeKind
  row: ChangeEntry
}

interface Target {
  kind: ChangeKind
  id: number
  /** Vrátenie tohto riadku je samo Redo - viď `RevertModal`. */
  isRedo: boolean
}

/**
 * Undo/Redo pre kalendár.
 *
 * Nie je to nový mechanizmus - je to skratka k tomu, čo už robí `/changelog`:
 * Späť otvorí potvrdenie pre najnovšiu vrátiteľnú zmenu (šípka rozbalí
 * zoznam, odkiaľ sa dá vybrať aj staršia alebo cudzia). Znova vráti riadok,
 * ktorý posledné vrátenie z tohto ovládača samo zanechalo v histórii - teda
 * vráti to vrátenie. Server žiadny samostatný pojem „redo“ nemá.
 */
export default function UndoRedoControl() {
  const { t } = useTranslation()
  const { data: boot } = useBootstrap()
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<Target | null>(null)
  const [redoTarget, setRedoTarget] = useState<{ kind: ChangeKind; id: number } | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const [mobileTop, setMobileTop] = useState<number | null>(null)

  // Na úzkej obrazovke sa zoznam ukotví k okrajom obrazovky pod tlačidlom.
  // Absolútne umiestnený by vyšiel za pravý okraj a rozšíril celú stránku
  // (telefón sa potom „oddiali“).
  useLayoutEffect(() => {
    if (!open || !boxRef.current || !window.matchMedia('(max-width: 639px)').matches) {
      setMobileTop(null)
      return
    }
    setMobileTop(unzoom(boxRef.current.getBoundingClientRect().bottom + 4))
  }, [open])

  // Bežia stále, nie len keď je zoznam otvorený - inak by hlavné tlačidlo
  // Späť ostalo neaktívne, kým ho operátor najprv nerozklikne.
  const queries = useQueries({
    queries: KINDS.map((kind) => ({
      queryKey: ['changelog', kind, '', 1, PER_KIND] as const,
      queryFn: () => changelogApi.list({ kind, limit: PER_KIND, offset: 0 }),
    })),
  })
  const loading = open && queries.some((q) => q.isLoading)

  const entries: Entry[] = queries
    .flatMap((q, i) => (q.data?.results ?? []).map((row) => ({ kind: KINDS[i], row })))
    .filter((entry) => entry.row.revertable)
    .sort((a, b) => (b.row.created ?? '').localeCompare(a.row.created ?? ''))
    .slice(0, LIMIT)
  const latest = entries[0] ?? null

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative flex items-center" ref={boxRef}>
      <div className="flex overflow-hidden rounded border border-slate-300">
        <button
          type="button"
          className="flex items-center gap-1.5 px-2 py-1 text-[13px] font-medium
                     text-slate-700 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40"
          disabled={!latest}
          title={t('calendar.undoHint')}
          onClick={() => {
            if (!latest) return
            setTarget({ kind: latest.kind, id: latest.row.id, isRedo: false })
            setOpen(false)
          }}
        >
          <Undo2 size={15} />
          <span className="hidden lg:inline">{t('calendar.undo')}</span>
        </button>
        <button
          type="button"
          className="border-l border-slate-300 px-1.5 py-1 text-slate-500 hover:bg-slate-50"
          title={t('calendar.recentChanges')}
          onClick={() => setOpen((v) => !v)}
        >
          <ChevronDown size={14} />
        </button>
      </div>

      <button
        type="button"
        className="ml-1.5 flex items-center gap-1.5 rounded border border-slate-300 px-2 py-1
                   text-[13px] font-medium text-slate-700 hover:bg-slate-50
                   disabled:pointer-events-none disabled:opacity-40"
        disabled={!redoTarget}
        title={t('calendar.redoHint')}
        onClick={() => {
          if (!redoTarget) return
          setTarget({ ...redoTarget, isRedo: true })
          setOpen(false)
        }}
      >
        <Redo2 size={15} />
        <span className="hidden lg:inline">{t('calendar.redo')}</span>
      </button>

      {open ? (
        <div
          className="absolute left-0 top-full z-20 mt-1 max-h-96 w-80 max-w-[calc(100vw-2rem)] overflow-y-auto
                     rounded-md border border-slate-200 bg-white shadow-lg"
          style={
            mobileTop !== null
              ? { position: 'fixed', top: mobileTop, left: 16, right: 16, width: 'auto' }
              : undefined
          }
        >
          {loading ? (
            <div className="flex h-20 items-center justify-center text-slate-400">
              <Spinner className="h-5 w-5" />
            </div>
          ) : entries.length === 0 ? (
            <EmptyState>{t('calendar.noRecentChanges')}</EmptyState>
          ) : (
            <ul>
              {entries.map((entry) => (
                <li key={`${entry.kind}-${entry.row.id}`}>
                  <button
                    type="button"
                    className="block w-full border-b border-slate-100 px-3 py-2 text-left
                               last:border-b-0 hover:bg-slate-50"
                    onClick={() => {
                      setTarget({ kind: entry.kind, id: entry.row.id, isRedo: false })
                      setOpen(false)
                    }}
                  >
                    <div className="flex items-baseline justify-between gap-2 text-[11px] text-slate-400">
                      <span className="flex min-w-0 items-baseline gap-1.5">
                        <span
                          title={t(`changelog.kind.${entry.kind}`)}
                          className={cn(
                            'shrink-0 rounded px-1 py-px text-[10px] font-bold',
                            KIND_STYLE[entry.kind],
                          )}
                        >
                          {t(`changelog.kindShort.${entry.kind}`)}
                        </span>
                        <span className="truncate text-slate-500">{entry.row.subject || '—'}</span>
                      </span>
                      <span className="shrink-0">{formatDateTime(entry.row.created)}</span>
                    </div>
                    {entry.row.context.start || entry.row.context.instructor ? (
                      // Termín rezervácie a inštruktor - aby bolo jasné, o ktorú
                      // lekciu ide, nie len o koho.
                      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-600">
                        <CalendarDays size={11} className="shrink-0 text-slate-400" />
                        <span>
                          {formatDateTime(entry.row.context.start) || '—'}
                          {entry.row.context.end ? `–${entry.row.context.end.slice(11, 16)}` : ''}
                        </span>
                        {entry.row.context.instructor ? (
                          <span className="truncate text-slate-500">· {entry.row.context.instructor}</span>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="mt-0.5">
                      <Changes entry={entry.row} kind={entry.kind} codebooks={boot} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {target ? (
        <RevertModal
          kind={target.kind}
          rowId={target.id}
          onClose={() => setTarget(null)}
          onReverted={(_message, newRowId) => {
            if (target.isRedo) {
              setRedoTarget(null)
            } else if (newRowId != null) {
              setRedoTarget({ kind: target.kind, id: newRowId })
            }
          }}
        />
      ) : null}
    </div>
  )
}
